import type { GaiaJsonValue } from '@/api';
import { isDatetimeString } from '@/utils/datetimeUtils';
import { safeStringify } from '@/utils/jsonHelpers';

/**
 * Checks if a string is likely a date
 * @param value String to check
 * @returns Boolean indicating if the string appears to be a date
 */
export function isDateString(value: string): boolean {
  // Check common date formats - ISO strings, common separators like /, - etc.
  const dateRegex = /^\d{4}[-/]\d{1,2}[-/]\d{1,2}|^\d{1,2}[-/]\d{1,2}[-/]\d{4}/;
  if (!dateRegex.test(value)) return false;

  // Try to parse it as a date and make sure it's valid
  const date = new Date(value);
  return !isNaN(date.getTime());
}

/**
 * Checks if an object is a currency amount object
 * @param value Object to check
 * @returns Boolean indicating if the object represents a currency amount
 */
export function isCurrencyAmount(value: Record<string, unknown>): boolean {
  return (
    'amount' in value &&
    'iso_4217_alpha_3' in value &&
    typeof value.amount === 'number' &&
    (typeof value.iso_4217_alpha_3 === 'string' || value.iso_4217_alpha_3 === null)
  );
}

/**
 * Checks if an object is a schedule object (legacy format)
 * @param value Object to check
 * @returns Boolean indicating if the object represents a schedule
 */
export function isSchedule(value: Record<string, unknown>): boolean {
  return (
    ('trigger_events' in value || 'calendar_events' in value) &&
    (value.trigger_events === null || Array.isArray(value.trigger_events)) &&
    (value.calendar_events === null || Array.isArray(value.calendar_events))
  );
}

/**
 * Checks if a value represents a CalendarEvent
 * @param value Object to check
 * @returns Boolean indicating if the object represents a CalendarEvent
 */
export function isCalendarEvent(value: Record<string, unknown>): boolean {
  // CalendarEvent must have event_date field
  // It may also have recurrence fields, but event_date is the key identifier
  return 'event_date' in value && typeof value.event_date === 'string';
}

/**
 * Checks if an object is an Entity
 * @param value Object to check
 * @returns Boolean indicating if the object represents an Entity
 */
export function isEntity(value: Record<string, unknown>): boolean {
  return (
    'name' in value &&
    typeof value.name === 'string' &&
    ('legal_name' in value ||
      'form_of_entity' in value ||
      'address' in value ||
      'email' in value ||
      'natural_person' in value)
  );
}

/**
 * Formats an Entity in a user-friendly way
 * @param entity The Entity object
 * @returns Formatted entity string
 */
export function formatEntity(entity: Record<string, unknown>): string {
  // Show legal_name if it exists and is not null, otherwise show name
  if (entity.legal_name && entity.legal_name !== '') {
    return entity.legal_name as string;
  }

  // Fallback to name if legal_name is not available
  return (entity.name as string) || '';
}

/**
 * Formats a currency amount in a user-friendly way
 * @param amount The amount number
 * @param currencyCode The ISO 4217 currency code
 * @returns Formatted currency string
 */
export function formatCurrency(amount: number, currencyCode: string | null): string {
  if (!currencyCode) return String(amount);

  try {
    // Use Intl.NumberFormat for proper currency formatting
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: currencyCode,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  } catch {
    // Fallback if currency code is invalid
    return `${amount.toLocaleString()} ${currencyCode}`;
  }
}

/**
 * Formats a CalendarEvent in a user-friendly way
 * Shows only the event date, similar to how entities show only the name
 * @param event The CalendarEvent object
 * @returns Formatted event string (just the date)
 */
export function formatCalendarEvent(event: Record<string, unknown>): string {
  // Show only the event date, similar to how entities show only the name
  if (event.event_date) {
    try {
      const date = new Date(event.event_date as string);
      if (!isNaN(date.getTime())) {
        // event_date is a date (no time component) — format date-only.
        // dateOnly=true also formats in UTC to avoid an off-by-one-day shift,
        // since "YYYY-MM-DD" parses as UTC midnight.
        return formatDate(date, true);
      }
    } catch {
      // If parsing fails, use original string
      return event.event_date as string;
    }
  }

  return 'No date';
}

/**
 * Formats a schedule in a user-friendly way (legacy format)
 * @param schedule The schedule object
 * @returns Formatted schedule string
 */
export function formatSchedule(schedule: Record<string, unknown>): string {
  const parts: string[] = [];

  // Handle trigger events
  if (schedule.trigger_events && Array.isArray(schedule.trigger_events)) {
    const triggerEvents = schedule.trigger_events as Record<string, unknown>[];
    if (triggerEvents.length > 0) {
      parts.push('Trigger Events:');
      for (const [index, event] of triggerEvents.entries()) {
        const description = event.description ? safeStringify(event.description) : 'No description';
        parts.push(`  ${index + 1}. ${description}`);
      }
    }
  }

  // Handle calendar events
  if (schedule.calendar_events && Array.isArray(schedule.calendar_events)) {
    const calendarEvents = schedule.calendar_events as Record<string, unknown>[];
    if (calendarEvents.length > 0) {
      if (parts.length > 0) parts.push('');
      parts.push('Calendar Events:');
      for (const [index, event] of calendarEvents.entries()) {
        const eventDate = event.event_date ? safeStringify(event.event_date) : 'No date';
        const summary = event.summary ? safeStringify(event.summary) : 'No summary';

        let eventStr = `  ${index + 1}. ${eventDate} - ${summary}`;

        const frequency = event.recurrence_frequency;
        if (frequency && frequency !== 'never') {
          let frequencyStr = frequency as string;

          // Add interval if it's not 1
          if (
            event.recurrence_interval &&
            typeof event.recurrence_interval === 'number' &&
            event.recurrence_interval > 1
          ) {
            frequencyStr = `every ${event.recurrence_interval} ${frequencyStr}s`;
          } else {
            frequencyStr = `every ${frequencyStr}`;
          }

          if (
            event.recurrence_type === 'count' &&
            event.recurrence_count &&
            typeof event.recurrence_count === 'number'
          ) {
            eventStr += `\n     Recurrence: repeats ${event.recurrence_count} times ${frequencyStr}`;
          } else if (event.recurrence_type === 'until_date' && event.recurrence_until_date) {
            eventStr += `\n     Recurrence: repeats ${frequencyStr} until ${safeStringify(event.recurrence_until_date)}`;
          } else {
            eventStr += `\n     Recurrence: repeats ${frequencyStr}`;
          }
        } else if (frequency === 'never') {
          eventStr += `\n     Recurrence: no recurrence (one-time event)`;
        }

        parts.push(eventStr);
      }
    }
  }

  return parts.length > 0 ? parts.join('\n') : 'Empty schedule';
}

/**
 * Formats a date in a readable way
 * @param date Date object to format
 * @returns Formatted date string
 */
export function formatDate(date: Date, dateOnly = false): string {
  try {
    // Use browser's default locale settings instead of hardcoded locale
    return new Intl.DateTimeFormat(undefined, {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      ...(dateOnly ? { timeZone: 'UTC' } : { hour: '2-digit', minute: '2-digit' }),
    }).format(date);
  } catch {
    return date.toISOString();
  }
}

const DATE_LIST_SEPARATOR = '; ';

/**
 * Determines the appropriate separator for joining array items based on their type
 * @param array The array of items to determine separator for
 * @returns Separator string: ', ' for special types (compact for table cells),
 *          '\n\n---\n\n' for complex objects (multi-line for detailed display)
 */
function getArraySeparator(array: unknown[]): string {
  if (array.length === 0) return ', ';

  const firstItem = array[0];
  // A formatted date ("June 30, 2027") carries its own comma, so dates are
  // split by semicolons to keep each day paired with its year.
  if (
    firstItem !== null &&
    typeof firstItem === 'object' &&
    isCalendarEvent(firstItem as Record<string, unknown>)
  ) {
    return DATE_LIST_SEPARATOR;
  }

  // Use compact separator for special types (better for table cells)
  if (
    firstItem !== null &&
    typeof firstItem === 'object' &&
    (isCurrencyAmount(firstItem as Record<string, unknown>) ||
      isEntity(firstItem as Record<string, unknown>))
  ) {
    return ', '; // Compact separator for table cells
  }

  return '\n\n---\n\n'; // Multi-line separator for complex objects
}

/**
 * Checks if a value is effectively empty (null, undefined, empty string, empty array, or empty object)
 * @param value The value to check
 * @returns True if the value is effectively empty
 */
export function isEffectivelyEmpty(value: unknown): boolean {
  if (value === null || value === undefined) return true;
  if (typeof value === 'string' && value.trim() === '') return true;
  if (Array.isArray(value) && value.length === 0) return true;
  if (typeof value === 'object') {
    const entries = Object.entries(value as Record<string, unknown>).filter(
      ([, v]) => v !== null && v !== undefined
    );
    return entries.length === 0;
  }
  return false;
}

/**
 * Formats a value for display in the UI based on field type
 * @param value The value to format
 * @param fieldType The field type to determine formatting (optional)
 * @returns Formatted string representation of the value
 */
export function formatDisplayValue(value: GaiaJsonValue, fieldType?: string | object): string {
  if (value === null || value === undefined) {
    return 'N/A';
  }

  // Handle boolean values
  if (typeof value === 'boolean') {
    return value ? 'Yes' : 'No';
  }

  // Handle numbers based on field type
  if (typeof value === 'number') {
    // If field type is explicitly a number type, don't treat as timestamp
    // EnumResponseType objects are not strings, so we only check string types here
    if (
      typeof fieldType === 'string' &&
      (fieldType === 'int' || fieldType === 'float' || fieldType === 'number')
    ) {
      return String(value);
    }

    // Check if it's likely a timestamp (reasonable range for dates)
    // Unix timestamps in milliseconds are typically between 1970 and 2100
    const minTimestamp = 0; // 1970-01-01
    const maxTimestamp = 4102444800000; // 2100-01-01

    if (value >= minTimestamp && value <= maxTimestamp) {
      try {
        const date = new Date(value);
        // Verify it's a valid date and not just a regular number
        if (!isNaN(date.getTime())) {
          return formatDate(date);
        }
      } catch {
        // If date parsing fails, treat as regular number
      }
    }

    return String(value);
  }

  // Handle date strings
  if (typeof value === 'string' && isDateString(value)) {
    try {
      const date = new Date(value);
      const dateOnly = fieldType === 'date' || (fieldType == null && !isDatetimeString(value));
      return formatDate(date, dateOnly);
    } catch {
      // If date parsing fails, keep original string
      return value;
    }
  }

  // Handle arrays - specifically check for arrays first
  if (Array.isArray(value)) {
    if (value.length === 0) {
      return 'Empty';
    }

    // Special handling for arrays of objects
    if (typeof value[0] === 'object' && value[0] !== null) {
      return value
        .map(item => {
          if (item === null || item === undefined) return null; // Will be filtered out

          // Format each object in the array
          if (typeof item === 'object') {
            const entries = Object.entries(item as Record<string, unknown>).filter(
              ([, val]) => val !== null && val !== undefined
            ); // Filter out null values

            if (entries.length === 0) return null; // Skip empty objects after filtering

            // Check if it's a currency amount object
            if (isCurrencyAmount(item as Record<string, unknown>)) {
              const amount = item.amount as number;
              const currency = item.iso_4217_alpha_3 as string | null;
              return formatCurrency(amount, currency);
            }

            // Check if it's a CalendarEvent object
            if (isCalendarEvent(item as Record<string, unknown>)) {
              return formatCalendarEvent(item as Record<string, unknown>);
            }

            // Check if it's an Entity object
            if (isEntity(item as Record<string, unknown>)) {
              return formatEntity(item as Record<string, unknown>);
            }

            // Check if it's a schedule object (legacy format)
            if (isSchedule(item as Record<string, unknown>)) {
              return formatSchedule(item as Record<string, unknown>);
            }

            // Create a user-friendly representation without curly braces
            return entries
              .map(([key, val]) => {
                const formattedKey = key.charAt(0).toUpperCase() + key.slice(1).replace(/_/g, ' ');
                // For specific fields that should always be treated as numbers, pass the appropriate field type
                let fieldTypeForValue = undefined;
                if (key === 'recurrence_interval' || key === 'recurrence_count') {
                  fieldTypeForValue = 'number';
                }
                return `${formattedKey}: ${formatDisplayValue(val, fieldTypeForValue)}`;
              })
              .join('\n');
          }
          return String(item);
        })
        .filter(item => item !== null) // Filter out null items
        .join(getArraySeparator(value));
    }

    const isListOfNumbers =
      typeof fieldType === 'string' &&
      (fieldType === 'list[int]' || fieldType === 'list[float]' || fieldType === 'list[number]');
    const isDateList = value.some(item => typeof item === 'string' && isDateString(item));
    return value
      .filter(item => item !== null && item !== undefined)
      .map(item => {
        if (isListOfNumbers && typeof item === 'number') {
          return formatDisplayValue(item, 'number');
        }
        return formatDisplayValue(item as GaiaJsonValue);
      })
      .join(isDateList ? DATE_LIST_SEPARATOR : ', ');
  }

  // Handle objects by joining their key-value pairs
  if (typeof value === 'object') {
    // Check if it's a currency amount object
    if (isCurrencyAmount(value as Record<string, unknown>)) {
      const amount = (value as Record<string, unknown>).amount as number;
      const currency = (value as Record<string, unknown>).iso_4217_alpha_3 as string | null;
      return formatCurrency(amount, currency);
    }

    // Check if it's a CalendarEvent object
    if (isCalendarEvent(value as Record<string, unknown>)) {
      return formatCalendarEvent(value as Record<string, unknown>);
    }

    // Check if it's an Entity object
    if (isEntity(value as Record<string, unknown>)) {
      return formatEntity(value as Record<string, unknown>);
    }

    // Check if it's a schedule object (legacy format)
    if (isSchedule(value as Record<string, unknown>)) {
      return formatSchedule(value as Record<string, unknown>);
    }

    const entries = Object.entries(value as Record<string, unknown>).filter(
      ([, val]) => val !== null && val !== undefined
    ); // Filter out null values

    if (entries.length === 0) {
      return 'N/A';
    }

    // Format object with key-value pairs in a user-friendly way without braces
    return entries
      .map(([key, val]) => {
        const formattedKey = key.charAt(0).toUpperCase() + key.slice(1).replace(/_/g, ' ');
        // For specific fields that should always be treated as numbers, pass the appropriate field type
        let fieldTypeForValue = undefined;
        if (key === 'recurrence_interval' || key === 'recurrence_count') {
          fieldTypeForValue = 'number';
        }
        return `${formattedKey}: ${formatDisplayValue(val, fieldTypeForValue)}`;
      })
      .join('\n');
  }

  // Handle strings - check for empty strings
  if (typeof value === 'string') {
    if (value.trim() === '') {
      return 'Empty';
    }
    return value;
  }

  // Handle numbers and other strings
  return safeStringify(value);
}

/**
 * Determines text color based on background color for optimal contrast
 * @param hexColor Background color in hex format
 * @returns Text color in hex format
 */
export function getContrastTextColor(hexColor: string): string {
  // Default to black text if no color provided or invalid format
  if (!hexColor || typeof hexColor !== 'string') {
    return '#000000';
  }

  // Handle white or very light colors explicitly
  if (hexColor.toLowerCase() === '#ffffff' || hexColor.toLowerCase() === '#fff') {
    return '#000000';
  }

  // Remove the hash if it's there
  const hex = hexColor.replace('#', '');

  // If hex isn't a valid length, return black as fallback
  if (![3, 6].includes(hex.length)) {
    return '#000000';
  }

  // Standardize 3-digit hex to 6-digit
  const fullHex = hex.length === 3 ? hex[0] + hex[0] + hex[1] + hex[1] + hex[2] + hex[2] : hex;

  // Convert hex to RGB
  const r = parseInt(fullHex.substring(0, 2), 16);
  const g = parseInt(fullHex.substring(2, 4), 16);
  const b = parseInt(fullHex.substring(4, 6), 16);

  // Handle NaN values from invalid hex
  if (isNaN(r) || isNaN(g) || isNaN(b)) {
    return '#000000';
  }

  // For very light colors (near white), always use black text
  if (r > 240 && g > 240 && b > 240) {
    return '#000000';
  }

  // Calculate luminance using the formula for relative luminance
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;

  // Higher threshold for better readability
  return luminance > 0.55 ? '#000000' : '#FFFFFF';
}
